import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFinance } from '../context/FinanceContext';
import { motion, AnimatePresence } from 'framer-motion';
import Modal from '../components/Modal';

export default function NewMonthWizard() {
  const { createMonth, months, fetchRecentMonths, stickyItems, fetchStickyItems } = useFinance();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const template = searchParams.get('template');
  
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [error, setError] = useState('');
  
  const [data, setData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    expected_income: '',
    paysRent: false,
    rent_amount: '',
    hasPrevDebts: false,
    carried_debts: [],
    hasNewDebts: false,
    new_debts: [],
    new_budgets: []
  });
  
  const [newDebtForm, setNewDebtForm] = useState({name: '', amount: '', notes: ''});
  const [newBudgetForm, setNewBudgetForm] = useState({name: '', amount: '', notes: ''});
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showDeferredModal, setShowDeferredModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [suggestedIncome, setSuggestedIncome] = useState(false);
  const [suggestedRent, setSuggestedRent] = useState(false);
  
  const autoFilled = useRef(false);

  const STEPS = [
    { id: 1, name: 'Month & Year', isRequired: true },
    { id: 2, name: 'Expected Income', isRequired: true },
    { id: 3, name: 'Rent', isRequired: false },
    { id: 4, name: 'Additional Debts', isRequired: false },
    { id: 5, name: 'Summary & Budgets', isRequired: true }
  ];

  useEffect(() => {
    fetchStickyItems();
    if (months.length === 0) {
      fetchRecentMonths();
    } else if (!autoFilled.current) {
      const lastMonth = months[0];
      if (lastMonth) {
        setData(prev => ({
          ...prev,
          expected_income: lastMonth.expected_income || prev.expected_income,
          paysRent: lastMonth.rent_amount > 0,
          rent_amount: lastMonth.rent_amount > 0 ? lastMonth.rent_amount : prev.rent_amount
        }));
        if (lastMonth.expected_income > 0) setSuggestedIncome(true);
        if (lastMonth.rent_amount > 0) setSuggestedRent(true);
      }
      autoFilled.current = true;
    }
  }, [months, fetchRecentMonths, fetchStickyItems]);

  const validateCurrentStep = () => {
    if (step === 1 && (!data.year || !data.month)) {
      setError('Please select a month and year.');
      return false;
    }
    if (step === 2 && (!data.expected_income || data.expected_income <= 0)) {
      setError('Please enter an income amount greater than K0.');
      return false;
    }
    if (step === 3 && data.paysRent && (!data.rent_amount || data.rent_amount <= 0)) {
      setError('Please enter your rent amount, or select No.');
      return false;
    }
    return true;
  };

  const handleStepClick = (targetStep) => {
    if (targetStep > step && !validateCurrentStep()) return;
    if (targetStep > 2 && (!data.expected_income || data.expected_income <= 0)) {
      setError('Please complete the Expected Income step first to skip ahead.');
      return;
    }
    setError('');
    setDirection(targetStep > step ? 1 : -1);
    setStep(targetStep);
  };

  const nextStep = () => {
    setError('');
    if (!validateCurrentStep()) return;

    if (step === 2 && template && data.new_budgets.length === 0) {
      let generatedBudgets = [];
      if (template === '50_30_20') {
        generatedBudgets = [
          { name: 'Needs (50%)', amount: (data.expected_income * 0.5).toFixed(2), notes: 'Essential living expenses' },
          { name: 'Wants (30%)', amount: (data.expected_income * 0.3).toFixed(2), notes: 'Discretionary spending' },
          { name: 'Savings/Debt (20%)', amount: (data.expected_income * 0.2).toFixed(2), notes: 'Future you' }
        ];
      } else if (template === 'minimalist') {
        generatedBudgets = [
          { name: 'Essentials', amount: (data.expected_income * 0.4).toFixed(2), notes: 'Groceries, Utilities, etc.' },
          { name: 'Bills', amount: (data.expected_income * 0.4).toFixed(2), notes: 'Fixed monthly bills' },
          { name: 'Savings', amount: (data.expected_income * 0.2).toFixed(2), notes: 'Emergency fund / Investments' }
        ];
      }
      setData(d => ({...d, new_budgets: generatedBudgets}));
    }

    setDirection(1);
    setStep(s => s + 1);
  };

  const prevStep = () => {
    setError('');
    setDirection(-1);
    setStep(s => s - 1);
  };

  const submit = async () => {
    try {
      const payload = { ...data };
      if(!payload.paysRent) payload.rent_amount = 0;
      const res = await createMonth(payload);
      navigate(`/month/${res.id}`);
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to create month');
    }
  };

  const variants = {
    enter: (direction) => {
      return {
        y: direction > 0 ? 200 : -200,
        opacity: 0
      };
    },
    center: {
      zIndex: 1,
      y: 0,
      opacity: 1
    },
    exit: (direction) => {
      return {
        zIndex: 0,
        y: direction < 0 ? 200 : -200,
        opacity: 0
      };
    }
  };

  const handleDragEnd = (event, info) => {
    const swipeThreshold = 30;
    if (info.offset.y < -swipeThreshold && step < 5) {
      if (validateCurrentStep()) nextStep();
    } else if (info.offset.y > swipeThreshold && step > 1) {
      prevStep();
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-0 md:gap-4 max-w-5xl mx-auto -mx-4 -mt-4 md:mx-auto md:mt-6 h-[calc(100vh-132px)] md:h-auto md:min-h-[450px]">
      
      {/* Sidebar (Desktop) */}
      <div className="hidden md:block w-64 shrink-0">
        <div className="bg-white rounded-3xl p-5 border border-gray-200 shadow-sm sticky top-6">
          <h2 className="font-bold text-gray-800 mb-4 text-lg">New Plan Setup</h2>
          <div className="space-y-2">
            {STEPS.map((s) => (
              <div 
                key={s.id} 
                onClick={() => handleStepClick(s.id)}
                className={`flex items-center gap-3 cursor-pointer p-2 rounded-2xl transition-all ${
                  step === s.id 
                    ? 'bg-blue-600 text-white shadow-sm transform scale-105' 
                    : 'text-gray-500 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs transition-colors ${
                  step === s.id ? 'bg-white text-blue-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {s.id}
                </div>
                <div>
                  <span className="block font-medium text-sm">{s.name}</span>
                  {s.isRequired 
                    ? <span className={`text-[9px] uppercase tracking-wider font-bold ${step === s.id ? 'text-blue-100' : 'text-red-400'}`}>Required</span> 
                    : <span className={`text-[9px] uppercase tracking-wider font-bold ${step === s.id ? 'text-blue-100' : 'text-gray-400'}`}>Optional</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-white rounded-none md:rounded-3xl shadow-none md:shadow-sm border-0 md:border border-gray-200 flex flex-col overflow-hidden relative">
        {/* Header / Mobile Step Indicator */}
        <div className="p-4 md:p-6 pb-3 border-b border-gray-100 z-10 bg-white relative flex justify-between items-start">
          <div>
            <div className="md:hidden flex items-center gap-2 mb-1">
              <span className="font-bold text-gray-800 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-[10px]">Step {step} of 5</span>
              <span className="text-[10px] font-semibold text-gray-500">{STEPS.find(s=>s.id===step).name}</span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800">
              {step === 1 && "Let's plan your month"}
              {step === 2 && "Income expectations"}
              {step === 3 && "Living arrangements"}
              {step === 4 && "Additional liabilities"}
              {step === 5 && "Review & finalize"}
              <span className="text-xs font-normal text-gray-500 block mt-0.5">
                {template === '50_30_20' ? '(50/30/20 Rule)' : template === 'minimalist' ? '(Minimalist Template)' : ''}
              </span>
            </h1>
          </div>
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-1 text-xs font-bold text-gray-400 hover:text-red-600 bg-gray-50 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all"
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="px-4 md:px-6 mt-3 z-10 relative">
            <div className="bg-red-50 text-red-600 p-2 rounded-lg text-xs font-medium border border-red-100">{error}</div>
          </div>
        )}

        <div className="flex-1 relative overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                y: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              className="absolute inset-0 p-4 md:p-6 overflow-y-auto no-scrollbar"
            >
              {/* --- STEP 1 --- */}
              {step === 1 && (
                <div className="space-y-4">
                  <label className="block text-gray-700 font-medium text-sm">Which month are you planning?</label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <select 
                      value={data.month} 
                      onChange={e => setData({...data, month: e.target.value})}
                      className="border border-gray-300 p-2 rounded-lg flex-1 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                    >
                      {Array.from({length: 12}).map((_, i) => (
                        <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', {month: 'long'})}</option>
                      ))}
                    </select>
                    <input 
                      type="number" 
                      value={data.year} 
                      onChange={e => setData({...data, year: e.target.value})}
                      className="border border-gray-300 p-2 rounded-lg w-full sm:w-24 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                    />
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-xs text-blue-800">
                      <strong>Tip:</strong> You can swipe up to go to the next step, or swipe down to go back on your phone!
                    </p>
                  </div>
                </div>
              )}

              {/* --- STEP 2 --- */}
              {step === 2 && (
                <div className="space-y-4">
                  <label className="block text-gray-700 font-medium text-sm">How much money do you expect this month?</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 font-bold text-sm">K</span>
                    <input 
                      type="number" 
                      value={data.expected_income} 
                      onChange={e => {
                        setData({...data, expected_income: e.target.value});
                        setSuggestedIncome(false);
                      }}
                      className="w-full border border-gray-300 p-2 pl-8 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all text-base font-medium"
                      placeholder="0.00"
                    />
                  </div>
                  {months.length > 0 && (() => {
                    const uniqueIncomes = [...new Set(
                      months.map(m => parseFloat(m.expected_income)).filter(v => v > 0)
                    )].sort((a, b) => b - a);
                    if (uniqueIncomes.length === 0) return null;
                    return (
                      <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                        <p className="text-[10px] text-gray-500 mb-2 font-semibold uppercase tracking-wider">📌 Pick from previous months:</p>
                        <div className="flex flex-wrap gap-2">
                          {uniqueIncomes.map((income, i) => {
                            const isSelected = parseFloat(data.expected_income) === income;
                            const matchedMonth = months.find(m => parseFloat(m.expected_income) === income);
                            const label = matchedMonth
                              ? new Date(0, matchedMonth.month - 1).toLocaleString('default', { month: 'short' }) + ' ' + matchedMonth.year
                              : '';
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => { setData({...data, expected_income: income}); setSuggestedIncome(true); }}
                                className={`flex flex-col items-start px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${
                                  isSelected
                                    ? 'bg-blue-50 text-blue-700 border-blue-500 shadow-sm transform scale-105'
                                    : 'bg-white text-gray-700 border-gray-100 hover:border-blue-200 hover:bg-gray-50'
                                }`}
                              >
                                <span>K{income.toLocaleString()}</span>
                                {label && <span className={`text-[9px] mt-0.5 font-semibold ${isSelected ? 'text-blue-500' : 'text-gray-400'}`}>{label}</span>}
                              </button>
                            );
                          })}
                        </div>
                        {suggestedIncome && <p className="text-[10px] text-green-600 mt-2 font-medium flex items-center gap-1">✓ Applied successfully</p>}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* --- STEP 3 --- */}
              {step === 3 && (
                <div className="space-y-4">
                  <label className="block text-gray-700 font-medium text-sm">Do you pay rent?</label>
                  <div className="flex gap-3 mb-1">
                    <button 
                      onClick={() => setData({...data, paysRent: true})} 
                      className={`flex-1 py-2 rounded-xl border-2 font-bold text-sm transition-all ${data.paysRent ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      Yes
                    </button>
                    <button 
                      onClick={() => setData({...data, paysRent: false})} 
                      className={`flex-1 py-2 rounded-xl border-2 font-bold text-sm transition-all ${!data.paysRent ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      No
                    </button>
                  </div>
                  
                  {data.paysRent && (
                     <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} className="space-y-3 pt-3 border-t border-gray-100">
                       <label className="block text-gray-700 font-medium text-xs">Rent Amount</label>
                       <div className="relative">
                         <span className="absolute left-3 top-2 text-gray-500 font-bold text-sm">K</span>
                         <input 
                           type="number" 
                           value={data.rent_amount} 
                           onChange={e => {
                             setData({...data, rent_amount: e.target.value});
                             setSuggestedRent(false);
                           }}
                           className="w-full border border-gray-300 p-2 pl-8 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all text-base font-medium"
                           placeholder="0.00"
                         />
                       </div>
                       
                       {months.length > 0 && (() => {
                         const uniqueRents = [...new Set(months.map(m => parseFloat(m.rent_amount)).filter(v => v > 0))].sort((a, b) => b - a);
                         if (uniqueRents.length === 0) return null;
                         return (
                           <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                             <p className="text-[10px] text-gray-500 mb-2 font-semibold uppercase tracking-wider">📌 Pick from previous rents:</p>
                             <div className="flex flex-wrap gap-2">
                               {uniqueRents.map((rent, i) => {
                                 const isSelected = parseFloat(data.rent_amount) === rent;
                                 return (
                                   <button
                                     key={i}
                                     type="button"
                                     onClick={() => { setData({...data, rent_amount: rent}); setSuggestedRent(true); }}
                                     className={`flex flex-col items-start px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${
                                       isSelected
                                         ? 'bg-blue-50 text-blue-700 border-blue-500 shadow-sm transform scale-105'
                                         : 'bg-white text-gray-700 border-gray-100 hover:border-blue-200 hover:bg-gray-50'
                                     }`}
                                   >
                                     <span>K{rent.toLocaleString()}</span>
                                   </button>
                                 );
                               })}
                             </div>
                             {suggestedRent && <p className="text-[10px] text-green-600 mt-2 font-medium">✓ Applied successfully</p>}
                           </div>
                         );
                       })()}
                     </motion.div>
                  )}
                </div>
              )}

              {/* --- STEP 4 --- */}
              {step === 4 && (
                <div className="space-y-4">
                  <label className="block text-gray-700 font-medium text-sm">Do you have additional debts for this month?</label>
                  <div className="flex gap-3 mb-1">
                    <button 
                      onClick={() => setData({...data, hasNewDebts: true})} 
                      className={`flex-1 py-2 rounded-xl border-2 font-bold text-sm transition-all ${data.hasNewDebts ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      Yes
                    </button>
                    <button 
                      onClick={() => setData({...data, hasNewDebts: false})} 
                      className={`flex-1 py-2 rounded-xl border-2 font-bold text-sm transition-all ${!data.hasNewDebts ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      No
                    </button>
                  </div>
                  
                  {data.hasNewDebts && (
                     <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} className="bg-gray-50 p-4 rounded-2xl border border-gray-200 mt-3">
                       <button onClick={() => setShowDebtModal(true)} className="w-full bg-blue-100 text-blue-700 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-200 transition-colors mb-2">
                         + Add Debt
                       </button>
                       {data.new_debts.length > 0 && (
                         <ul className="space-y-2 mt-2">
                           {data.new_debts.map((d, i) => (
                             <li key={i} className="flex justify-between items-center bg-white p-2 border border-gray-100 rounded-lg shadow-sm text-sm">
                               <span className="font-medium text-gray-700">{d.name}</span>
                               <span className="font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-md">K{d.amount}</span>
                             </li>
                           ))}
                         </ul>
                       )}

                       <Modal isOpen={showDebtModal} onClose={() => setShowDebtModal(false)} title="Add Debt">
                         <div className="grid gap-3 mb-2">
                           <input placeholder="Debt Name" value={newDebtForm.name} onChange={e => setNewDebtForm({...newDebtForm, name: e.target.value})} className="border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" />
                           <input placeholder="Amount" type="number" value={newDebtForm.amount} onChange={e => setNewDebtForm({...newDebtForm, amount: e.target.value})} className="border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" />
                           <input placeholder="Notes" value={newDebtForm.notes} onChange={e => setNewDebtForm({...newDebtForm, notes: e.target.value})} className="border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" />
                           <button onClick={() => {
                             if(!newDebtForm.name || !newDebtForm.amount) return;
                             setData({...data, new_debts: [...data.new_debts, newDebtForm]});
                             setNewDebtForm({name: '', amount: '', notes: ''});
                             setShowDebtModal(false);
                           }} className="bg-blue-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors mt-2">Add Debt</button>
                         </div>
                       </Modal>
                     </motion.div>
                  )}
                </div>
              )}

              {/* --- STEP 5 --- */}
              {step === 5 && (
                <div className="space-y-6 pb-6">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800 mb-3">Summary & Review</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-gray-50 p-4 rounded-2xl border border-gray-200">
                      <div className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                        <div>
                          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Month</p>
                          <p className="font-bold text-gray-800">{new Date(0, data.month - 1).toLocaleString('default', {month: 'long'})} {data.year}</p>
                        </div>
                        <button onClick={() => handleStepClick(1)} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded">Edit</button>
                      </div>
                      <div className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                        <div>
                          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Expected Income</p>
                          <p className="font-bold text-blue-600 text-sm">K{data.expected_income || 0}</p>
                        </div>
                        <button onClick={() => handleStepClick(2)} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded">Edit</button>
                      </div>
                      <div className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                        <div>
                          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Rent</p>
                          <p className="font-bold text-gray-800">{data.paysRent ? `K${data.rent_amount}` : 'None'}</p>
                        </div>
                        <button onClick={() => handleStepClick(3)} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded">Edit</button>
                      </div>
                      <div className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                        <div>
                          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">New Debts</p>
                          <p className="font-bold text-red-600">{data.hasNewDebts && data.new_debts.length > 0 ? `${data.new_debts.length} debt(s)` : 'None'}</p>
                        </div>
                        <button onClick={() => handleStepClick(4)} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded">Edit</button>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons for Step 5 */}
                  <div className="flex flex-wrap gap-3 mt-6">
                    {stickyItems && stickyItems.filter(i => i.type === 'plan').length > 0 && (
                      <button onClick={() => setShowPlansModal(true)} className="flex-1 min-w-[140px] bg-blue-50 text-blue-700 py-2.5 rounded-xl text-xs font-bold hover:bg-blue-100 transition-colors">
                        Review Future Plans
                      </button>
                    )}
                    {stickyItems && stickyItems.filter(i => i.type === 'debt').length > 0 && (
                      <button onClick={() => setShowDeferredModal(true)} className="flex-1 min-w-[140px] bg-red-50 text-red-700 py-2.5 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors">
                        Review Deferred Debts
                      </button>
                    )}
                    <button onClick={() => setShowBudgetModal(true)} className="flex-1 min-w-[140px] bg-gray-50 text-gray-700 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-100 transition-colors border border-gray-200">
                      {data.new_budgets.length > 0 ? 'Edit Initial Budgets' : 'Add Initial Budgets'}
                    </button>
                  </div>

                  {/* Future Plans Modal */}
                  <Modal isOpen={showPlansModal} onClose={() => setShowPlansModal(false)} title="Future Plans">
                    <div className="space-y-2">
                      {stickyItems?.filter(i => i.type === 'plan').map(plan => (
                        <div key={plan.id} className="flex justify-between items-center bg-gray-50 p-3 border border-gray-100 rounded-lg shadow-sm">
                          <div>
                            <span className="font-bold block text-sm text-gray-800">{plan.name}</span>
                            <span className="text-xs text-gray-500 font-medium">K{plan.amount} {plan.notes ? `- ${plan.notes}` : ''}</span>
                          </div>
                          <button 
                            onClick={() => {
                              setData(prev => ({...prev, new_budgets: [...prev.new_budgets, {name: plan.name, amount: plan.amount, notes: plan.notes}]}));
                              setShowPlansModal(false);
                            }} 
                            className="text-xs bg-blue-600 text-white font-bold px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                          >
                            Add to Budget
                          </button>
                        </div>
                      ))}
                    </div>
                  </Modal>

                  {/* Deferred Debts Modal */}
                  <Modal isOpen={showDeferredModal} onClose={() => setShowDeferredModal(false)} title="Deferred Debts">
                    <div className="space-y-2">
                      {stickyItems?.filter(i => i.type === 'debt').map(debt => (
                        <div key={debt.id} className="flex justify-between items-center bg-gray-50 p-3 border border-gray-100 rounded-lg shadow-sm">
                          <div>
                            <span className="font-bold block text-sm text-gray-800">{debt.name}</span>
                            <span className="text-xs text-gray-500 font-medium">K{debt.amount} {debt.notes ? `- ${debt.notes}` : ''}</span>
                          </div>
                          <button 
                            onClick={() => {
                              setData(prev => ({...prev, new_debts: [...prev.new_debts, {name: debt.name, amount: debt.amount, notes: debt.notes}], hasNewDebts: true}));
                              setShowDeferredModal(false);
                            }} 
                            className="text-xs bg-red-600 text-white font-bold px-3 py-1.5 rounded hover:bg-red-700 transition-colors"
                          >
                            Add to Debts
                          </button>
                        </div>
                      ))}
                    </div>
                  </Modal>

                  {/* Initial Budgets Modal */}
                  <Modal isOpen={showBudgetModal} onClose={() => setShowBudgetModal(false)} title="Initial Budgets">
                    <div className="grid gap-3 mb-4">
                      <input placeholder="Name" value={newBudgetForm.name} onChange={e => setNewBudgetForm({...newBudgetForm, name: e.target.value})} className="border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" />
                      <input placeholder="Amount" type="number" value={newBudgetForm.amount} onChange={e => setNewBudgetForm({...newBudgetForm, amount: e.target.value})} className="border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" />
                      <input placeholder="Notes" value={newBudgetForm.notes} onChange={e => setNewBudgetForm({...newBudgetForm, notes: e.target.value})} className="border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm" />
                      <button onClick={() => {
                        if(!newBudgetForm.name || !newBudgetForm.amount) return;
                        setData({...data, new_budgets: [...data.new_budgets, newBudgetForm]});
                        setNewBudgetForm({name: '', amount: '', notes: ''});
                      }} className="bg-blue-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors">Add Budget</button>
                    </div>
                    {data.new_budgets.length > 0 && (
                      <ul className="space-y-2 pt-3 border-t border-gray-200">
                        {data.new_budgets.map((b, i) => (
                          <li key={i} className="flex justify-between items-center bg-gray-50 p-3 border border-gray-100 rounded-lg shadow-sm">
                            <div>
                              <span className="font-bold block text-sm text-gray-800">{b.name}</span>
                              <span className="text-xs text-gray-500 font-medium">{b.notes}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded text-sm">K{b.amount}</span>
                              <button onClick={() => {
                                const arr = [...data.new_budgets];
                                arr.splice(i, 1);
                                setData({...data, new_budgets: arr});
                              }} className="text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 px-2 py-1 rounded">Remove</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Modal>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer Navigation Buttons */}
        <div className="p-3 md:p-4 pt-2 border-t border-gray-100 flex justify-between z-10 bg-white relative">
          {step > 1 ? (
            <button onClick={prevStep} className="px-4 py-1.5 border-2 border-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-50 hover:border-gray-300 font-bold transition-all w-1/3 sm:w-auto">Back</button>
          ) : <div></div>}
          
          {step < 5 ? (
            <button onClick={nextStep} className="px-5 py-1.5 bg-blue-600 text-white text-xs rounded-lg shadow hover:bg-blue-700 font-bold transition-all transform hover:scale-105 active:scale-95 w-1/2 sm:w-auto">Next Step</button>
          ) : (
            <button onClick={submit} className="px-5 py-1.5 bg-green-600 text-white text-xs rounded-lg shadow hover:bg-green-700 font-bold transition-all transform hover:scale-105 active:scale-95 w-1/2 sm:w-auto">Create Month</button>
          )}
        </div>
      </div>
    </div>
  );
}
